import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  SHIPPING_BOX_FILL_PERCENT,
  SHIPPING_SETTINGS_ROUTES,
  shippingSettingsInputSchema,
  shippingSettingsResponseSchema,
  type ShippingSettings,
  type ShippingSettingsInput,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, CheckboxField, Form, SelectField, SubmitButton, TextField } from "@/shared/ui/core";
import { SkeletonCard } from "@/shared/ui/ui";
import {
  AdminGuard,
  FormSection,
  FormSectionStack,
  NumberField,
  RepeatableList,
} from "@/features/admin/components";
import { useSedes } from "@/features/admin/sedes/sedes.queries";

/**
 * ═══════════════════════════════════════════════════════════════════
 * ENVÍOS (Anahí, 13-oct-2026): de dónde salen los pedidos y en qué
 * cajas viajan.
 *
 * El checkout cotiza con Trasso desde la SEDE DE ORIGEN, y la caja que
 * se cotiza es la más chica de esta lista donde cabe el pedido —por
 * volumen, al {{fill}} % y con el frasco más grande entrando lado por
 * lado—. Sin sede, el checkout no cobra envío y se coordina a mano;
 * sin cajas, se cotiza la suma por volumen de siempre.
 * ═══════════════════════════════════════════════════════════════════
 */

const NO_PREFIX = { baseUrl: "" } as const;
const KEY = ["admin", "shipping-settings"] as const;

async function getSettings(): Promise<ShippingSettings> {
  return (await authFetch(SHIPPING_SETTINGS_ROUTES.admin, shippingSettingsResponseSchema, NO_PREFIX))
    .settings;
}

async function saveSettings(input: ShippingSettingsInput): Promise<ShippingSettings> {
  return (
    await authFetch(SHIPPING_SETTINGS_ROUTES.admin, shippingSettingsResponseSchema, {
      ...NO_PREFIX,
      method: "PUT",
      body: input,
    })
  ).settings;
}

/** Texto del campo → número; vacío o basura → NaN, que el esquema rechaza con palabras. */
const decimal = (message: string) =>
  z
    .string()
    .trim()
    .min(1, message)
    .refine((raw) => Number.isFinite(Number(raw.replace(",", "."))) && Number(raw.replace(",", ".")) > 0, {
      message: "Escribe un número mayor que cero",
    });

const boxFormSchema = z.object({
  name: z.string().trim().min(1, "Ponle nombre a la caja").max(60),
  lengthCm: decimal("Escribe el largo"),
  widthCm: decimal("Escribe el ancho"),
  heightCm: decimal("Escribe el alto"),
  emptyWeightGrams: z
    .string()
    .trim()
    .regex(/^\d{1,5}$/, "Gramos enteros"),
  active: z.boolean(),
});

const formSchema = z.object({
  originSedeId: z.string(),
  boxes: z.array(boxFormSchema).max(20, "Máximo 20 cajas"),
});

type FormValues = z.infer<typeof formSchema>;

const num = (raw: string) => Number(raw.trim().replace(",", "."));

function toForm(settings: ShippingSettings): FormValues {
  return {
    originSedeId: settings.originSedeId ?? "",
    boxes: settings.boxes.map((box) => ({
      name: box.name,
      lengthCm: String(box.lengthCm),
      widthCm: String(box.widthCm),
      heightCm: String(box.heightCm),
      emptyWeightGrams: String(box.emptyWeightGrams),
      active: box.active,
    })),
  };
}

export function AdminShippingPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: KEY, queryFn: getSettings });
  const sedes = useSedes({ page: 1, perPage: 100, status: "active" });
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { originSedeId: "", boxes: [] },
  });

  useEffect(() => {
    if (settings.data) form.reset(toForm(settings.data));
  }, [settings.data, form]);

  const save = useMutation({
    mutationFn: saveSettings,
    onSuccess: (data) => {
      queryClient.setQueryData(KEY, data);
      setSaved(true);
    },
    onError: (cause) => setError(cause instanceof Error ? cause.message : t("No se pudo guardar")),
  });

  const sedeOptions = useMemo(
    () =>
      (sedes.data?.items ?? []).map((sede) => ({
        value: sede.id,
        label:
          sede.zip && sede.phone && sede.state
            ? `${sede.name} · ${sede.city}`
            : `${sede.name} · ${sede.city} — ${t("le falta estado, CP o teléfono")}`,
      })),
    [sedes.data, t],
  );

  function enviar(values: FormValues) {
    setError(null);
    setSaved(false);
    // El MISMO esquema que el servidor: un nombre repetido rebota aquí.
    const parsed = shippingSettingsInputSchema.safeParse({
      originSedeId: values.originSedeId || null,
      boxes: values.boxes.map((box) => ({
        name: box.name.trim(),
        lengthCm: num(box.lengthCm),
        widthCm: num(box.widthCm),
        heightCm: num(box.heightCm),
        emptyWeightGrams: Number(box.emptyWeightGrams),
        active: box.active,
      })),
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("Revisa las cajas"));
      return;
    }
    save.mutate(parsed.data);
  }

  return (
    <AdminGuard>
      <PageHeader
        titleHidden
        title={t("Envíos")}
        description={t(
          "De dónde salen los pedidos y en qué cajas viajan. Con esto el checkout cotiza el envío con Trasso y el comprador lo paga junto con su pedido.",
        )}
      />

      {settings.isLoading ? (
        <SkeletonCard />
      ) : (
        <Form form={form} onSubmit={enviar}>
          <FormSectionStack>
            <FormSection
              title={t("Sede de origen")}
              description={t(
                "La dirección desde la que se cotiza el envío en el checkout. Necesita estado, código postal y teléfono. Sin sede, el envío no se cobra al comprar y se coordina a mano.",
              )}
              columns={2}
            >
              <SelectField
                name="originSedeId"
                label={t("Sede")}
                placeholder={t("Sin sede: el envío se coordina a mano")}
                options={sedeOptions}
              />
            </FormSection>

            <FormSection
              title={t("Cajas de la bodega")}
              description={t(
                "Medidas INTERIORES en centímetros y lo que pesa vacía en gramos. Para cada pedido se elige la caja más chica donde caben los frascos (llenándola hasta el {{fill}} %). Si no cabe en ninguna, se cotiza una caja calculada por volumen.",
                { fill: SHIPPING_BOX_FILL_PERCENT },
              )}
            >
              <RepeatableList
                name="boxes"
                addLabel={t("Agregar caja")}
                itemLabel={(index) => t("Caja {{n}}", { n: index + 1 })}
                newItem={() => ({
                  name: "",
                  lengthCm: "",
                  widthCm: "",
                  heightCm: "",
                  emptyWeightGrams: "",
                  active: true,
                })}
                max={20}
                emptyHint={t("Todavía no hay cajas: se cotiza la suma por volumen.")}
              >
                {({ name }) => (
                  <div className="grid gap-3 sm:grid-cols-[1.4fr_repeat(4,1fr)_auto] sm:items-end">
                    <TextField name={`${name}.name`} label={t("Nombre")} placeholder={t("Chica")} />
                    <NumberField name={`${name}.lengthCm`} label={t("Largo (cm)")} inputMode="decimal" />
                    <NumberField name={`${name}.widthCm`} label={t("Ancho (cm)")} inputMode="decimal" />
                    <NumberField name={`${name}.heightCm`} label={t("Alto (cm)")} inputMode="decimal" />
                    <NumberField name={`${name}.emptyWeightGrams`} label={t("Peso vacía (g)")} />
                    <CheckboxField name={`${name}.active`} label={t("En uso")} />
                  </div>
                )}
              </RepeatableList>
            </FormSection>

            {error && (
              <Alert variant="error" onDismiss={() => setError(null)}>
                {error}
              </Alert>
            )}
            {saved && <Alert variant="success">{t("Guardado. El siguiente checkout ya cotiza con esto.")}</Alert>}

            <div>
              <SubmitButton loading={save.isPending}>{t("Guardar envíos")}</SubmitButton>
            </div>
          </FormSectionStack>
        </Form>
      )}
    </AdminGuard>
  );
}
